import { useState, useEffect } from 'react';
import './BusyOverlay.css';

const BUSY_EVENT = 'fokus:busy';

let depth = 0;

/**
 * Show the full-screen busy overlay (darkens screen, blocks interaction).
 * Calls are reference-counted, so nested show/hide pairs behave correctly.
 *
 * @param {string} [message='Working…'] - Text shown beneath the spinner.
 * @returns {() => void} A function that hides this overlay request.
 */
export function showBusyOverlay(message = 'Working…') {
  if (typeof window === 'undefined') return () => {};
  depth += 1;
  window.dispatchEvent(
    new CustomEvent(BUSY_EVENT, { detail: { visible: true, message } })
  );
  let hidden = false;
  return () => {
    if (hidden) return;
    hidden = true;
    hideBusyOverlay();
  };
}

/**
 * Hide one busy overlay request. The overlay only disappears once all
 * outstanding requests have been hidden.
 */
export function hideBusyOverlay() {
  if (typeof window === 'undefined') return;
  depth = Math.max(0, depth - 1);
  if (depth === 0) {
    window.dispatchEvent(
      new CustomEvent(BUSY_EVENT, { detail: { visible: false } })
    );
  }
}

/**
 * Run an async task with the busy overlay shown for its duration.
 *
 * @template T
 * @param {() => Promise<T>} task
 * @param {string} [message]
 * @returns {Promise<T>}
 */
export async function withBusyOverlay(task, message) {
  const hide = showBusyOverlay(message);
  try {
    return await task();
  } finally {
    hide();
  }
}

/**
 * Global busy overlay container. Render once near the app root. Listens for
 * `fokus:busy` window events and shows a blocking, dimmed loading screen.
 */
export default function BusyOverlay() {
  const [state, setState] = useState({ visible: false, message: 'Working…' });

  useEffect(() => {
    const handler = (e) => {
      const { visible, message } = e.detail || {};
      setState((prev) => ({
        visible: !!visible,
        message: message || prev.message,
      }));
    };
    window.addEventListener(BUSY_EVENT, handler);
    return () => window.removeEventListener(BUSY_EVENT, handler);
  }, []);

  if (!state.visible) return null;

  return (
    <div className="fokus-busy-overlay" role="alertdialog" aria-busy="true" aria-live="assertive">
      <div className="fokus-busy-overlay__panel">
        <div className="fokus-busy-overlay__spinner" />
        <div className="fokus-busy-overlay__message">{state.message}</div>
      </div>
    </div>
  );
}
